'use client';

import { CheckCircle, XCircle } from 'lucide-react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { plural } from '@/lib/i18n/pluralization';
import type { ReviewQuestion } from '@/lib/tests/testQuestions';

interface TestReviewQuestionProps {
  question: ReviewQuestion;
  index: number;
  userAnswer: string | string[] | undefined;
}

/**
 * One question of a finished attempt. The review never knows the answer key:
 * the server says only whether this question was answered correctly, and
 * sends the explanation only for correctly answered questions. So the
 * participant's own choices are marked right or wrong, and the correct
 * option of a wrongly answered question is never shown.
 */
export default function TestReviewQuestion({ question, index, userAnswer }: TestReviewQuestionProps) {
  const { t } = useLanguage();
  const points = question.points ?? 1;
  const verdict = question.answered_correctly;
  const hasAnswer = Array.isArray(userAnswer) ? userAnswer.length > 0 : !!userAnswer;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="font-semibold text-sm text-gray-500">
          {t('testTaking.question')} {index + 1} ({points} {plural(points, t('testTaking.pointOne'), t('testTaking.pointFew'), t('testTaking.pointMany'))})
        </h3>
        {verdict !== null && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium flex-shrink-0 ${
              verdict ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}
          >
            {verdict ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
            {verdict ? t('event.test.answeredCorrectly') : t('event.test.answeredIncorrectly')}
          </span>
        )}
      </div>
      <p className="text-gray-900 mb-4">{question.question_text}</p>

      {question.type === 'essay' ? (
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm font-medium text-gray-500 mb-1">{t('event.test.yourAnswer')}:</p>
          <p className="text-gray-800 whitespace-pre-wrap">{(userAnswer as string) || '–'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {question.options.map((option) => {
            const isSelected = question.type === 'multiple_choice'
              ? Array.isArray(userAnswer) && userAnswer.includes(option.id)
              : userAnswer === option.id;
            // Only the participant's own choices are coloured, by the verdict
            // of the whole question.
            const tone = !isSelected
              ? 'border-gray-200'
              : verdict ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50';

            return (
              <div key={option.id} className={`flex items-start gap-3 p-3 border rounded-lg ${tone}`}>
                <input
                  type={question.type === 'multiple_choice' ? 'checkbox' : 'radio'}
                  checked={isSelected}
                  disabled
                  readOnly
                  className="h-4 w-4 mt-0.5 flex-shrink-0"
                />
                <span className="flex-1 break-words">{option.option_text}</span>
                {isSelected && verdict === true && (
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                )}
                {isSelected && verdict === false && (
                  <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                )}
              </div>
            );
          })}
          {!hasAnswer && (
            <p className="text-sm text-gray-500">{t('event.test.noAnswerGiven')}</p>
          )}
        </div>
      )}

      {question.explanation && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">{question.explanation}</p>
        </div>
      )}
    </div>
  );
}
